import React from 'react';
import View from "react-native-web/dist/exports/View";
import Platform from "react-native-web/dist/exports/Platform";
import StyleSheet from "react-native-web/dist/exports/StyleSheet";
import PropTypes from 'prop-types';
import StackView from '../StackView';
import Typography from '../Typography';
import RenderActivityIndicator from './RenderActivityIndicator';
import { BACKDROP_Z_INDEX, BACKDROP_OPACITY, SPACE_WITH_LABEL, SPACE_WITHOUT_LABEL } from './shared';
import { activityIndicatorCommonProps } from './sharedProptypes';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const recursiveMap = (nodeChildren, fn) => React.Children.map(nodeChildren, child => {
  if (! /*#__PURE__*/React.isValidElement(child)) return child;
  if (child.props?.children) {
    return fn(/*#__PURE__*/React.cloneElement(child, {
      children: recursiveMap(child.props.children, fn)
    }));
  }
  return fn(child);
});
const OverlayIndicator = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
    variantProps,
    label,
    labelPosition,
    labelMapping,
    children,
    inline,
    showLabel,
    isActive = true
  } = _ref;
  const safeChildren = Platform.OS === 'web' && isActive ? recursiveMap(children, c => /*#__PURE__*/React.isValidElement(c) ? /*#__PURE__*/React.cloneElement(c, {
    role: 'presentation'
  }) : c) : children;

  // inline + children
  if (inline && children) {
    return /*#__PURE__*/_jsxs(View, {
      style: [staticStyles.container, Platform.OS === 'web' && staticStyles.inlineContainer],
      children: [/*#__PURE__*/_jsx(View, {
        style: staticStyles.backdropContainer,
        children: safeChildren
      }), /*#__PURE__*/_jsx(View, {
        style: staticStyles.buttonOverlaySpinner,
        children: /*#__PURE__*/_jsx(RenderActivityIndicator, {
          ...variantProps,
          ref: ref,
          label: label
        })
      })]
    });
  }

  // overlay
  return /*#__PURE__*/_jsxs(View, {
    style: staticStyles.container,
    children: [/*#__PURE__*/_jsx(View, {
      style: staticStyles.backdropContainer,
      children: safeChildren
    }), /*#__PURE__*/_jsx(View, {
      style: [staticStyles.overlayContainer, Platform.OS === 'web' && staticStyles.webOverlayContainer],
      children: /*#__PURE__*/_jsxs(StackView, {
        space: showLabel ? SPACE_WITH_LABEL : SPACE_WITHOUT_LABEL,
        tokens: {
          alignItems: 'center'
        },
        direction: labelMapping[labelPosition],
        children: [/*#__PURE__*/_jsx(RenderActivityIndicator, {
          ...variantProps,
          ref: ref,
          label: label
        }), showLabel && /*#__PURE__*/_jsx(Typography, {
          children: label
        })]
      })
    })]
  });
});
OverlayIndicator.displayName = 'OverlayIndicator';
const staticStyles = StyleSheet.create({
  container: {
    position: 'relative'
  },
  inlineContainer: {
    display: 'inline-flex'
  },
  backdropContainer: {
    opacity: BACKDROP_OPACITY
  },
  overlayContainer: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    ...Platform.select({
      ios: {
        zIndex: BACKDROP_Z_INDEX
      },
      android: {
        elevation: 5
      }
    })
  },
  webOverlayContainer: {
    transform: [{
      translateX: '-50%'
    }, {
      translateY: '-50%'
    }],
    zIndex: BACKDROP_Z_INDEX
  },
  buttonOverlaySpinner: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    ...Platform.select({
      web: {
        transform: 'translate(-50%, -50%)',
        zIndex: BACKDROP_Z_INDEX
      },
      default: {
        transform: [{
          translateX: -0.5
        }, {
          translateY: -0.5
        }],
        zIndex: BACKDROP_Z_INDEX
      }
    })
  }
});
OverlayIndicator.propTypes = {
  /**
   * Shared props
   * */
  ...activityIndicatorCommonProps,
  /**
   * Background color for overlay
   * */
  inline: PropTypes.bool,
  /**
   * Children on which the overlay is drawn
   * */
  children: PropTypes.node,
  /**
   * Controls whether the overlay is active and should apply the presentation role
   * */
  isActive: PropTypes.bool
};
export default OverlayIndicator;